<template>
  <div class="member-visitors-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Visitors"
      :columns="visitorColumns"
      :items="visitors"
      :loading="loading"
      :total-items="totalItems"
      :page.sync="options.page"
      :items-per-page.sync="options.itemsPerPage"
      :searchable="false"
    >
      <!-- Custom Cells -->
      <template #col-profile_url="{ item }">
        <div class="profile-cell">
          <div class="p-avatar-wrap">
            <img 
              v-if="item.profile_url" 
              :src="API_BASE_URL + '/' + item.profile_url" 
              class="p-avatar-img"
            />
            <div v-else class="p-avatar-placeholder" :style="{ background: primaryColor + '20', color: primaryColor }">
              {{ item.name ? item.name.charAt(0).toUpperCase() : 'V' }}
            </div>
          </div>
          <div class="p-info">
            <span class="p-name">{{ item.name }}</span>
            <span class="p-id" v-if="item.mobile_no">{{ item.mobile_no }}</span>
          </div>
        </div>
      </template>

      <template #col-purpose="{ item }">
        <div class="purpose-cell">
          <span class="purpose-pill" v-if="item.purpose && item.purpose !== 'null'">
            {{ item.purpose }}
          </span>
          <span class="dash" v-else>—</span>
        </div>
      </template>

      <template #col-created_at="{ item }">
        <span class="date-text">{{ item.created_at | formatDateTime }}</span>
      </template>

      <template #col-status="{ item }">
        <div class="status-pill-small" :class="getStatusClass(item.status)">
          {{ item.status || 'Pending' }}
        </div>
      </template>
    </PremiumTable>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import PremiumTable from '../../common/PremiumTable.vue';

export default {
  name: 'MemberVisitors',

  components: { PremiumTable },

  props: {
    member_id: { type: [String, Number], required: true }
  },

  data() {
    return {
      loading:    false,
      visitors:   [],
      totalItems: 0,
      options: {
        page:         1,
        itemsPerPage: 10
      },
      API_BASE_URL: process.env.VUE_APP_API_URL,
      visitorColumns: [
        { key: 'profile_url', label: 'Visitor Details', width: '250px' },
        { key: 'purpose',     label: 'Purpose of Visit' },
        { key: 'created_at',  label: 'Entry Time', width: '200px' },
        { key: 'status',      label: 'Status', width: '140px' },
      ]
    };
  },

  computed: {
    ...mapState('theme', ['primaryColor']),
  },

  watch: {
    'options.page'() { this.fetchVisitors(); },
    'options.itemsPerPage'() { this.fetchVisitors(); }
  },

  created() {
    this.fetchVisitors();
  },

  methods: {
    async fetchVisitors() {
      this.loading = true;
      try {
        const response = await this.$axios.get(`societyadmin/members/${this.member_id}/visitors`, {
          params: {
            page:    this.options.page,
            perPage: this.options.itemsPerPage
          }
        });
        if (response.data.status) {
          this.visitors = response.data.visitors;
          this.totalItems = response.data.total;
        }
      } catch (error) {
        console.error("Error fetching visitors:", error);
      } finally {
        this.loading = false;
      }
    },

    getStatusClass(status) {
      if (!status) return 'status-pending';
      const s = status.toLowerCase();
      if (s === 'accepted' || s === 'approved') return 'status-approved';
      if (s === 'rejected' || s === 'cancelled') return 'status-rejected';
      return 'status-pending';
    },


  }
};
</script>

<style scoped>
.table-header-box { display: flex; align-items: center; justify-content: space-between; }
.th-title-wrap { display: flex; align-items: center; }
.th-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 16px; font-weight: 700; color: var(--tx);
  letter-spacing: -0.02em; margin: 0;
}

/* ── Profile Cell ── */
.profile-cell { display: flex; align-items: center; gap: 12px; }
.p-avatar-wrap { width: 42px; height: 42px; flex-shrink: 0; min-width: 42px; border-radius: 50%; overflow: hidden; background: var(--surface2); border: 1px solid var(--border); }
.p-avatar-img { width: 100%; height: 100%; object-fit: cover; }
.p-avatar-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 800; }

.p-info { display: flex; flex-direction: column; gap: 2px; }
.p-name { font-size: 13.5px; font-weight: 700; color: var(--tx); }
.p-id { font-size: 11px; font-weight: 500; color: var(--tx3); }

/* ── Purpose ── */
.purpose-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--surface2, #F1F5F9);
  color: var(--tx2, #475569);
  font-size: 11px;
  font-weight: 600;
  border: 1px solid var(--border);
}

.date-text { font-size: 12.5px; color: var(--tx3); font-weight: 500; white-space: nowrap; display: block; }

/* Status Pills */
.status-pill-small {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: capitalize;
}
.status-pill-small::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  background: currentColor;
  opacity: 0.8;
}
.status-approved { 
  background: rgba(16, 185, 129, 0.1); 
  color: #10B981; 
  border: 1px solid rgba(16, 185, 129, 0.2);
}
.status-rejected { 
  background: rgba(239, 68, 68, 0.1); 
  color: #EF4444; 
  border: 1px solid rgba(239, 68, 68, 0.2);
}
.status-pending  { 
  background: rgba(245, 158, 11, 0.1); 
  color: #F59E0B; 
  border: 1px solid rgba(245, 158, 11, 0.2);
}
</style>
